import React from "react";
import './SearchView.css';
import BackendService, { BackendLockedError } from "../../model/services/BackendService";
import { Alert } from "@material-ui/lab";
import { Box, Button, ButtonBase, ButtonGroup, CircularProgress, Grid, List, ListItem, ListItemAvatar, ListItemText, Typography } from "@material-ui/core";
import { localizedStrings } from "../../util/LocalizedStrings";
import NavigationManager from "../../util/NavigationManager";
import ExpandMoreIcon from '@material-ui/icons/ExpandMoreRounded';
import Ad, { AdType } from '../../view/Ad';

export default class SearchView extends React.Component {
    resultsPageSize = 10
    backendService = new BackendService()
    searchTypes = {
        BING: 'bing',
        FACE_SHERLOCK: 'face_sherlock',
        ACTOR_SHERLOCK: 'actor_sherlock'
    }

    constructor(props) {
        super(props);
        this.state = {
            isLoading: false,
            sameResults: [],
            similarResults: [],
            sameResultsToShowCount: this.resultsPageSize,
            similarResultsToShowCount: this.resultsPageSize,
            isSearchLocked: false,
            didFinishLoading: false
        }
    }

    componentDidMount() {
        this.search()
    }

    render() {
        return <div className='SearchView'>
            {this.renderProgress()}
            {this.renderError()}
            {this.renderSearchLocked()}
            {this.renderSameResults()}
            {this.renderSimilarResults()}
            {this.renderAd()}
            {this.renderExternalSearchButtons()}
        </div>
    }

    async search() {
        this.setState({ isLoading: true, error: null, isSearchLocked: false });
        try {
            const searchResultsId = this.props.searchResultsId;
            const [sameResults, similarResults] = searchResultsId
                ? await this.backendService.getSearchResults(searchResultsId)
                : await this.backendService.searchImage(this.props.imageUrl);
            this.setState({
                sameResults: sameResults,
                similarResults: similarResults
            });
        } catch (error) {
            if (error instanceof BackendLockedError) {
                this.setState({ isSearchLocked: true });
            } else {
                this.setState({ error: error.message });
            }
        } finally {
            this.setState({ isLoading: false, didFinishLoading: true });
        }
    }

    renderProgress() {
        if (!this.state.isLoading) return null
        return <CircularProgress className='SearchViewProgress' color='secondary' />
    }

    renderError() {
        let error = this.state.error
        if (error == null) return null;
        return <div>
            <Alert style={{ margin: 8 }} severity="error">
                {error}
            </Alert>
            {this.renderRetryButton()}
        </div>
    }

    renderSearchLocked() {
        if (!this.state.isSearchLocked) return
        return <div>
            <Alert style={{ margin: 8 }}
                severity="error"
                action={
                    <Button
                        color="secondary"
                        variant="contained"
                        disableElevation 
                        size="small"
                        onClick={() => window.open(NavigationManager.getAppStoreLink())}>
                        {localizedStrings.searchViewGetApp}
                    </Button>
                }>
                {localizedStrings.searchViewSearchLocked}
            </Alert>
            {this.renderRetryButton()}
        </div>
    }

    renderRetryButton() {
        return <Button variant="contained" disableElevation
            onClick={() => this.search()}>{localizedStrings.searchViewRetry}
        </Button>
    }

    renderSameResults() {
        const resultsToShowCount = this.state.sameResultsToShowCount
        let results = this.state.sameResults.slice(0, resultsToShowCount)
        if (results.length === 0) return
        return <div>
            <Box display="flex" justifyContent="center">
                <List>
                    {results.map((item, i) =>
                        <ListItem button alignItems="flex-start"
                            key={i}
                            style={{ padding: '2px 8px 6px', borderRadius: 8 }}
                            onClick={() => NavigationManager.openUrlInNewTab(item.page_url)}>
                            <ListItemAvatar>
                                <img className='SearchViewSameItemImage' src={item.image_url} />
                            </ListItemAvatar>
                            <ListItemText primary={item.title} secondary={item.host_name} />
                        </ListItem>)}
                </List>
            </Box>
            {this.renderPaginationButtons(resultsToShowCount, true)}
        </div>
    }

    renderPaginationButtons(resultsToShowCount, forSameResults) {
        const resultsCount = (forSameResults
            ? this.state.sameResults
            : this.state.similarResults).length
        const hasMoreItems = resultsToShowCount < resultsCount
        const resultsToShowCountKey = forSameResults ? 'sameResultsToShowCount' : 'similarResultsToShowCount'
        return hasMoreItems && <div className='SearchViewPaginationButtons'>
            <Button variant='contained' disableElevation
                style={{ padding: '4px 8px' }}
                onClick={() =>
                    this.setState({ [resultsToShowCountKey]: resultsCount })}>
                {localizedStrings.searchViewShowAll}
                {<ExpandMoreIcon />}
            </Button>
            <Button variant='contained' disableElevation
                style={{ padding: '4px 8px' }}
                onClick={() =>
                    this.setState({ [resultsToShowCountKey]: resultsToShowCount + this.resultsPageSize })}>
                {localizedStrings.searchViewShowMore}
                {<ExpandMoreIcon />}
            </Button>
        </div>
    }

    async searchImage(type) {
        var url
        var imageUrl = encodeURIComponent(this.props.imageUrl)
        switch (type) {
            case this.searchTypes.BING:
                url = 'https://www.bing.com/images/search?view=detailv2&iss=sbi&form=SBIIDP&sbisrc=UrlPaste&idpbck=1&selectedindex=0&vt=2&sim=11&q=imgurl:'
                    + imageUrl + '&vsimg=' + imageUrl + '&id=' + imageUrl + '&mediaurl=' + imageUrl
                break
            case this.searchTypes.FACE_SHERLOCK:
                url = 'https://facesherlock.com/?image_url=' + imageUrl + '&source=photo_sherlock'
                break
            case this.searchTypes.ACTOR_SHERLOCK:
                url = 'https://actorsherlock.com/redirect'
                break
            default: break
        }
        window.open(url)
    }

    renderExternalSearchButtons() {
        if (!this.state.didFinishLoading) return
        return <div className='ExternalSearchButtons'>
            <span>{localizedStrings.searchViewSearchWith}</span>
            <ButtonGroup variant="contained" color='secondary' orientation="vertical">
                <Button onClick={() => this.searchImage(this.searchTypes.BING)}>
                    {localizedStrings.searchImagePageSearchInBingButton}
                </Button>
                <Button onClick={() => this.searchImage(this.searchTypes.FACE_SHERLOCK)}>
                    {localizedStrings.searchImagePageSearchWithFaceSherlockButton}
                </Button>
                <Button onClick={() => this.searchImage(this.searchTypes.ACTOR_SHERLOCK)}>
                    {localizedStrings.searchImagePageSearchWithActorSherlockButton}
                </Button>
            </ButtonGroup>
        </div>
    }

    renderSimilarResults() {
        const resultsToShowCount = this.state.similarResultsToShowCount
        let results = this.state.similarResults.slice(0, resultsToShowCount)
        if (results.length === 0) return
        return <div>
            <h4>{localizedStrings.searchViewSimilarImages}</h4>
            <Grid>
                {results.map((item, i) => {
                    const imageUrl = item.image_url
                    return <ButtonBase
                        key={i}
                        style={{ borderRadius: 8 }}
                        disabled={item.inProgress}
                        onClick={() => NavigationManager.openImageSearchUrl(imageUrl)}>
                        <div className='SearchViewSimilarItem'>
                            <img className='SearchItemGridImage' src={imageUrl} />
                            <Typography className='SearchViewSimilarItemHostName'
                                variant='body2'
                                style={{ fontSize: 12, margin: '0 4px' }}>
                                {item.host_name}
                            </Typography>
                        </div>
                    </ButtonBase>;
                }
                )}
            </Grid>
            {this.renderPaginationButtons(resultsToShowCount, false)}
        </div>
    }

    renderAd() {
        if (this.state.sameResults.length === 0 && this.state.similarResults.length === 0) return null;
        return <div className="SearchViewAd"><Ad type={AdType.RESPONSIVE_2} /></div>;
    }
}